import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Grid, List, MoreVertical, Plus, Users, TrendingUp, TrendingDown, ChevronDown, ChevronRight, Briefcase, CheckCircle, DollarSign, X, Trash2, Edit, Eye, Upload, Camera, Pencil, Check, Loader2, Building2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/UserAuthContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const API_URL = (() => {
  // Always use the current origin for API calls - runtime detection
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
})();

const RealEstateClientsCustomers = () => {
  const { user } = useAuth();
  const userId = user?.user_id || user?.email || '';
  
  const [activeTab, setActiveTab] = useState('Clients');
  const [clientFilter, setClientFilter] = useState('A-Z');
  const [customerFilter, setCustomerFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  const customerFileInputRef = useRef(null);

  // Card-level photo upload state
  const [cardUploadTarget, setCardUploadTarget] = useState(null); // { id, type: 'client'|'customer' }
  const [uploadingCardId, setUploadingCardId] = useState(null);
  const cardPhotoInputRef = useRef(null);

  const [clients, setClients] = useState([]);
  const [customers, setCustomers] = useState([]);

  // Fetch clients from database
  const fetchClients = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_URL}/api/client-list/clients?user_id=${encodeURIComponent(userId)}`);
      if (response.ok) {
        const data = await response.json();
        // Map API response to frontend expected structure
        const mappedClients = (data.clients || []).map(client => ({
          ...client,
          type: client.subtitle || client.type || '',
          budget: client.budget || client.revenue || 0,
          projects: client.projects || 0,
          tasks: client.tasks || 0,
          employees: client.employees || 0,
          logo: client.logo || (client.name ? client.name.substring(0, 2).toUpperCase() : 'CO'),
          color: client.color || 'bg-purple-600'
        }));
        setClients(mappedClients);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    }
  }, [userId]);

  // Fetch customers from database
  const fetchCustomers = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_URL}/api/customer-list/customers?user_id=${encodeURIComponent(userId)}`);
      if (response.ok) {
        const data = await response.json();
        // Map API response to frontend expected structure
        const mappedCustomers = (data.customers || []).map(customer => ({
          ...customer,
          totalOrder: customer.totalOrder || customer.spent || 0,
          avgOrder: customer.avgOrder || 0,
          orders: customer.orders || customer.purchases || 0,
          odId: customer.odId || customer.id?.substring(0, 5) || String(70000 + Math.floor(Math.random() * 10000)),
          initials: customer.initials || (customer.name ? customer.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : 'CU'),
          lastOrder: customer.lastOrder || customer.member_since || 'N/A'
        }));
        setCustomers(mappedCustomers);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    }
  }, [userId]);

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      if (!userId) return;
      setLoading(true);
      await Promise.all([fetchClients(), fetchCustomers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchClients, fetchCustomers, userId]);

  // Default client filter tabs
  const defaultClientFilters = [
    { id: 'A-Z', name: 'A-Z', icon: '🔤', is_default: true },
    { id: 'Budget', name: 'Budget', icon: '💰', is_default: true },
    { id: 'Projects', name: 'Projects', icon: '📁', is_default: true },
    { id: 'Tasks', name: 'Tasks', icon: '✅', is_default: true }
  ];
  
  // Default customer filter tabs
  const defaultCustomerFilters = [
    { id: 'All', name: 'All Customers', icon: '👥', is_default: true },
    { id: 'Active', name: 'Active', icon: '✅', is_default: true },
    { id: 'Inactive', name: 'Inactive', icon: '⏸️', is_default: true }
  ];
  
  // Categories stored in database
  const [customClientFilters, setCustomClientFilters] = useState(defaultClientFilters);
  const [customCustomerFilters, setCustomCustomerFilters] = useState(defaultCustomerFilters);
  
  // Fetch categories from database on mount
  const fetchClientCategories = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_URL}/api/categories/client?user_id=${encodeURIComponent(userId)}`);
      if (response.ok) {
        const data = await response.json();
        setCustomClientFilters(data.categories || defaultClientFilters);
      }
    } catch (error) {
      console.error('Failed to fetch client categories:', error);
    }
  }, [userId]);

  const fetchCustomerCategories = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${API_URL}/api/categories/customer?user_id=${encodeURIComponent(userId)}`);
      if (response.ok) {
        const data = await response.json();
        setCustomCustomerFilters(data.categories || defaultCustomerFilters);
      }
    } catch (error) {
      console.error('Failed to fetch customer categories:', error);
    }
  }, [userId]);

  // Load categories on mount
  useEffect(() => {
    if (userId) {
      fetchClientCategories();
      fetchCustomerCategories();
    }
  }, [fetchClientCategories, fetchCustomerCategories, userId]);
  
  // Modal states for editing categories
  const [showClientFilterModal, setShowClientFilterModal] = useState(false);
  const [showCustomerFilterModal, setShowCustomerFilterModal] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  const [newFilterIcon, setNewFilterIcon] = useState('📋');
  const [editingFilter, setEditingFilter] = useState(null);
  const [editFilterValue, setEditFilterValue] = useState('');
  
  const filterIcons = ['📋', '🔤', '💰', '📁', '✅', '👥', '⏸️', '🏆', '⭐', '🎯', '📊', '📈', '🔥', '💼', '🏢', '👤', '📅', '🔔', '💡', '🎨'];
  
  // Client filter management - save to database
  const addClientFilter = async () => {
    if (!newFilterName.trim()) return;
    if (!userId) { toast.error('Please log in'); return; }
    if (customClientFilters.some(f => f.id === newFilterName)) { toast.error('Filter already exists'); return; }
    
    try {
      const response = await fetch(`${API_URL}/api/categories/client?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newFilterName, icon: newFilterIcon, type: 'client' })
      });
      
      if (response.ok) {
        const newFilter = { id: newFilterName, name: newFilterName, icon: newFilterIcon, is_default: false };
        setCustomClientFilters(prev => [...prev, newFilter]);
        setNewFilterName('');
        setNewFilterIcon('📋');
        toast.success(`Added "${newFilterName}" category`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to add category');
      }
    } catch (error) {
      console.error('Failed to add client filter:', error);
      toast.error('Failed to add category');
    }
  };
  
  const updateClientFilter = async (oldId, newName) => {
    if (!newName.trim()) return;
    if (!userId) { toast.error('Please log in'); return; }
    
    // Check if it's a default filter
    const filter = customClientFilters.find(f => f.id === oldId);
    if (filter?.is_default) { toast.error('Cannot rename default category'); return; }
    
    try {
      const response = await fetch(`${API_URL}/api/categories/client/${encodeURIComponent(oldId)}?user_id=${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName })
      });
      
      if (response.ok) {
        const updated = customClientFilters.map(f => f.id === oldId ? { ...f, id: newName, name: newName } : f);
        setCustomClientFilters(updated);
        if (clientFilter === oldId) setClientFilter(newName);
        setEditingFilter(null);
        setEditFilterValue('');
        toast.success(`Category renamed to "${newName}"`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to rename category');
      }
    } catch (error) {
      console.error('Failed to update client filter:', error);
      toast.error('Failed to rename category');
    }
  };
  
  const deleteClientFilter = async (filterId) => {
    if (!userId) { toast.error('Please log in'); return; }
    
    // Check if it's a default filter
    const filter = customClientFilters.find(f => f.id === filterId);
    if (filter?.is_default) { toast.error('Cannot delete default category'); return; }
    
    try {
      const response = await fetch(`${API_URL}/api/categories/client/${encodeURIComponent(filterId)}?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        const updated = customClientFilters.filter(f => f.id !== filterId);
        setCustomClientFilters(updated);
        if (clientFilter === filterId) setClientFilter('A-Z');
        toast.success('Category deleted');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Failed to delete client filter:', error);
      toast.error('Failed to delete category');
    }
  };
  
  // Customer filter management - save to database
  const addCustomerFilter = async () => {
    if (!newFilterName.trim()) return;
    if (!userId) { toast.error('Please log in'); return; }
    if (customCustomerFilters.some(f => f.id === newFilterName)) { toast.error('Filter already exists'); return; }
    
    try {
      const response = await fetch(`${API_URL}/api/categories/customer?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newFilterName, icon: newFilterIcon, type: 'customer' })
      });
      
      if (response.ok) {
        const newFilter = { id: newFilterName, name: newFilterName, icon: newFilterIcon, is_default: false };
        setCustomCustomerFilters(prev => [...prev, newFilter]);
        setNewFilterName('');
        setNewFilterIcon('📋');
        toast.success(`Added "${newFilterName}" category`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to add category');
      }
    } catch (error) {
      console.error('Failed to add customer filter:', error);
      toast.error('Failed to add category');
    }
  };
  
  const updateCustomerFilter = async (oldId, newName) => {
    if (!newName.trim()) return;
    if (!userId) { toast.error('Please log in'); return; }
    
    // Check if it's a default filter
    const filter = customCustomerFilters.find(f => f.id === oldId);
    if (filter?.is_default) { toast.error('Cannot rename default category'); return; }
    
    try {
      const response = await fetch(`${API_URL}/api/categories/customer/${encodeURIComponent(oldId)}?user_id=${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName })
      });
      
      if (response.ok) {
        const updated = customCustomerFilters.map(f => f.id === oldId ? { ...f, id: newName, name: newName } : f);
        setCustomCustomerFilters(updated);
        if (customerFilter === oldId) setCustomerFilter(newName);
        setEditingFilter(null);
        setEditFilterValue('');
        toast.success(`Category renamed to "${newName}"`);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to rename category');
      }
    } catch (error) {
      console.error('Failed to update customer filter:', error);
      toast.error('Failed to rename category');
    }
  };
  
  const deleteCustomerFilter = async (filterId) => {
    if (!userId) { toast.error('Please log in'); return; }
    
    // Check if it's a default filter
    const filter = customCustomerFilters.find(f => f.id === filterId);
    if (filter?.is_default) { toast.error('Cannot delete default category'); return; }
    
    try {
      const response = await fetch(`${API_URL}/api/categories/customer/${encodeURIComponent(filterId)}?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        const updated = customCustomerFilters.filter(f => f.id !== filterId);
        setCustomCustomerFilters(updated);
        if (customerFilter === filterId) setCustomerFilter('All');
        toast.success('Category deleted');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Failed to delete customer filter:', error);
      toast.error('Failed to delete category');
    }
  };

  const colorOptions = [
    { value: 'bg-purple-600', label: 'Purple' },
    { value: 'bg-blue-600', label: 'Blue' },
    { value: 'bg-green-600', label: 'Green' },
    { value: 'bg-orange-600', label: 'Orange' },
    { value: 'bg-red-600', label: 'Red' },
    { value: 'bg-pink-600', label: 'Pink' },
    { value: 'bg-cyan-600', label: 'Cyan' },
    { value: 'bg-yellow-600', label: 'Yellow' },
    { value: 'bg-indigo-600', label: 'Indigo' }
  ];

  const [newClient, setNewClient] = useState({
    name: '',
    type: '',
    projects: 0,
    tasks: 0,
    employees: 0,
    budget: 0,
    color: 'bg-purple-600',
    image: null,
    email: '',
    phone: '',
    address: '',
    category: '' // Category for filtering
  });

  const [newCustomer, setNewCustomer] = useState({
    name: '',
    firstName: '',
    lastName: '',
    status: 'Active',
    totalOrder: 0,
    avgOrder: 0,
    orders: 0,
    avatar: null,
    email: '',
    phone: '',
    address: '',
    // New fields from file import
    productType: '',
    productName: '',
    date: '',
    payment: 0,
    bookingUrl: '',
    category: '' // Category for filtering
  });

  // File import states
  const [showImportModal, setShowImportModal] = useState(false);
  const [importedCustomers, setImportedCustomers] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const importFileInputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const generateLogo = (name) => {
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const generateInitials = (name) => {
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleImageUpload = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'client') {
          setNewClient({ ...newClient, image: reader.result });
        } else if (type === 'customer') {
          setNewCustomer({ ...newCustomer, avatar: reader.result });
        } else if (type === 'details-client') {
          setSelectedItem({ ...selectedItem, image: reader.result });
        } else if (type === 'details-customer') {
          setSelectedItem({ ...selectedItem, avatar: reader.result });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Resize image to compact base64 JPEG for storage in MongoDB
  const resizeImageToBase64 = (file, maxDim = 400, quality = 0.75) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let w = img.width, h = img.height;
          if (w > maxDim || h > maxDim) {
            if (w > h) { h = Math.round(h * maxDim / w); w = maxDim; }
            else { w = Math.round(w * maxDim / h); h = maxDim; }
          }
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // Handle direct photo upload from card hover overlay
  const handleCardPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !cardUploadTarget) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be less than 10MB'); return; }

    const { id, type } = cardUploadTarget;
    setUploadingCardId(id);

    try {
      const dataUrl = await resizeImageToBase64(file);
      if (type === 'client') {
        const res = await fetch(`${API_URL}/api/client-list/clients/${id}?user_id=${encodeURIComponent(userId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ image: dataUrl })
        });
        if (!res.ok) throw new Error('Failed to save photo');
        setClients(prev => prev.map(c => c.id === id ? { ...c, image: dataUrl } : c));
      } else {
        const res = await fetch(`${API_URL}/api/customer-list/customers/${id}?user_id=${encodeURIComponent(userId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ avatar: dataUrl })
        });
        if (!res.ok) throw new Error('Failed to save photo');
        setCustomers(prev => prev.map(c => c.id === id ? { ...c, avatar: dataUrl } : c));
      }
      toast.success('Photo saved permanently!');
    } catch (err) {
      console.error('Card photo upload error:', err);
      toast.error('Failed to update photo');
    } finally {
      setUploadingCardId(null);
      setCardUploadTarget(null);
      if (cardPhotoInputRef.current) cardPhotoInputRef.current.value = '';
    }
  };

  const handleAddClient = async () => {
    if (!newClient.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!newClient.type.trim()) {
      toast.error('Company type is required');
      return;
    }
    if (!userId) {
      toast.error('Please log in to add clients');
      return;
    }

    const clientData = {
      name: newClient.name,
      subtitle: newClient.type,
      type: newClient.type,
      logo: newClient.image || generateLogo(newClient.name),
      image: newClient.image,
      email: newClient.email || null,
      phone: newClient.phone || null,
      address: newClient.address || null,
      projects: Number(newClient.projects) || 0,
      tasks: Number(newClient.tasks) || 0,
      employees: Number(newClient.employees) || 0,
      budget: Number(newClient.budget) || 0,
      revenue: Number(newClient.budget) || 0,
      color: newClient.color,
      status: 'Active',
      category: newClient.category || null
    };

    try {
      if (editItem) {
        // Update existing client
        const response = await fetch(`${API_URL}/api/client-list/clients/${editItem.id}?user_id=${encodeURIComponent(userId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(clientData)
        });
        
        if (response.ok) {
          const data = await response.json();
          const clientResponse = data.client;
          // Map API response to frontend expected structure
          const mappedClient = {
            ...clientResponse,
            type: clientResponse.subtitle || clientResponse.type || '',
            budget: clientResponse.budget || clientResponse.revenue || 0,
            projects: clientResponse.projects || 0,
            tasks: clientResponse.tasks || 0,
            employees: clientResponse.employees || 0,
            logo: clientResponse.logo || generateLogo(clientResponse.name),
            color: clientResponse.color || newClient.color || 'bg-purple-600',
            category: clientResponse.category || ''
          };
          setClients(prev => prev.map(c => c.id === editItem.id ? mappedClient : c));
          toast.success('Company updated successfully');
        } else {
          const err = await response.text();
          console.error('Failed to update client:', err);
          toast.error('Failed to update company');
          return;
        }
      } else {
        // Create new client
        const response = await fetch(`${API_URL}/api/client-list/clients?user_id=${encodeURIComponent(userId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(clientData)
        });
        
        if (response.ok) {
          const data = await response.json();
          const clientResponse = data.client;
          // Map API response to frontend expected structure
          const mappedClient = {
            ...clientResponse,
            type: clientResponse.subtitle || clientResponse.type || '',
            budget: clientResponse.budget || clientResponse.revenue || 0,
            projects: clientResponse.projects || 0,
            tasks: clientResponse.tasks || 0,
            employees: clientResponse.employees || 0,
            logo: clientResponse.logo || generateLogo(clientResponse.name),
            color: clientResponse.color || newClient.color || 'bg-purple-600',
            category: clientResponse.category || ''
          };
          setClients(prev => [mappedClient, ...prev]);
          toast.success('Company added successfully');
        } else {
          const err = await response.text();
          console.error('Failed to add client:', err);
          toast.error('Failed to add company');
          return;
        }
      }
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error('Failed to save company');
      return;
    }

    setShowAddModal(false);
    setEditItem(null);
    setNewClient({ name: '', type: '', projects: 0, tasks: 0, employees: 0, budget: 0, color: 'bg-purple-600', image: null, email: '', phone: '', address: '', category: '' });
  };

  const handleAddCustomer = async () => {
    // Allow name to be auto-generated from firstName + lastName
    const fullName = newCustomer.name.trim() || 
      `${newCustomer.firstName.trim()} ${newCustomer.lastName.trim()}`.trim();
    
    if (!fullName) {
      toast.error('Customer name is required (or provide First and Last name)');
      return;
    }
    if (!userId) {
      toast.error('Please log in to add customers');
      return;
    }

    const customerData = {
      name: fullName,
      first_name: newCustomer.firstName || null,
      last_name: newCustomer.lastName || null,
      email: newCustomer.email || null,
      phone: newCustomer.phone || null,
      avatar: newCustomer.avatar,
      address: newCustomer.address || null,
      totalOrder: Number(newCustomer.totalOrder) || Number(newCustomer.payment) || 0,
      avgOrder: Number(newCustomer.avgOrder) || Number(newCustomer.payment) || 0,
      orders: Number(newCustomer.orders) || (newCustomer.payment ? 1 : 0),
      spent: Number(newCustomer.totalOrder) || Number(newCustomer.payment) || 0,
      purchases: Number(newCustomer.orders) || (newCustomer.productName ? 1 : 0),
      status: newCustomer.status || 'Active',
      // New fields from file import
      product_type: newCustomer.productType || null,
      product_name: newCustomer.productName || null,
      date: newCustomer.date || null,
      payment: Number(newCustomer.payment) || null,
      booking_url: newCustomer.bookingUrl || null,
      category: newCustomer.category || null
    };

    try {
      if (editItem) {
        // Update existing customer
        const response = await fetch(`${API_URL}/api/customer-list/customers/${editItem.id}?user_id=${encodeURIComponent(userId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(customerData)
        });
        
        if (response.ok) {
          const data = await response.json();
          const customerResponse = data.customer;
          // Map API response to frontend expected structure
          const mappedCustomer = {
            ...customerResponse,
            totalOrder: customerResponse.totalOrder || customerResponse.spent || 0,
            avgOrder: customerResponse.avgOrder || 0,
            orders: customerResponse.orders || customerResponse.purchases || 0,
            initials: generateInitials(customerResponse.name),
            odId: editItem.odId || customerResponse.id?.substring(0, 5) || String(70000 + Math.floor(Math.random() * 10000)),
            lastOrder: customerResponse.lastOrder || customerResponse.member_since || 'N/A'
          };
          setCustomers(prev => prev.map(c => c.id === editItem.id ? mappedCustomer : c));
          toast.success('Customer updated successfully');
        } else {
          const err = await response.text();
          console.error('Failed to update customer:', err);
          toast.error('Failed to update customer');
          return;
        }
      } else {
        // Create new customer
        const response = await fetch(`${API_URL}/api/customer-list/customers?user_id=${encodeURIComponent(userId)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(customerData)
        });
        
        if (response.ok) {
          const data = await response.json();
          const customerResponse = data.customer;
          // Map API response to frontend expected structure
          const mappedCustomer = {
            ...customerResponse,
            totalOrder: customerResponse.totalOrder || customerResponse.spent || 0,
            avgOrder: customerResponse.avgOrder || 0,
            orders: customerResponse.orders || customerResponse.purchases || 0,
            initials: generateInitials(customerResponse.name),
            odId: customerResponse.id?.substring(0, 5) || String(70000 + Math.floor(Math.random() * 10000)),
            lastOrder: new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
          };
          setCustomers(prev => [mappedCustomer, ...prev]);
          toast.success('Customer added successfully');
        } else {
          const err = await response.text();
          console.error('Failed to add customer:', err);
          toast.error('Failed to add customer');
          return;
        }
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      toast.error('Failed to save customer');
      return;
    }

    setShowAddCustomerModal(false);
    setEditItem(null);
    setNewCustomer({ name: '', firstName: '', lastName: '', status: 'Active', totalOrder: 0, avgOrder: 0, orders: 0, avatar: null, email: '', phone: '', address: '', productType: '', productName: '', date: '', payment: 0, bookingUrl: '', category: '' });
  };

  // Handle file upload for customer import
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/pdf'
    ];
    
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['csv', 'xlsx', 'xls', 'pdf'].includes(ext)) {
      toast.error('Please upload a CSV, Excel (.xlsx), or PDF file');
      return;
    }

    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/customer-import/parse`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      
      if (response.ok && data.status === 'success') {
        setImportedCustomers(data.customers);
        setShowImportModal(true);
        toast.success(`Found ${data.count} customer(s) in file`);
      } else if (data.status === 'warning') {
        toast.warning(data.message);
        setImportedCustomers([]);
      } else {
        toast.error(data.detail || 'Failed to parse file');
      }
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setIsImporting(false);
      if (importFileInputRef.current) {
        importFileInputRef.current.value = '';
      }
    }
  };

  // Import all parsed customers
  const handleImportCustomers = async () => {
    if (!userId || importedCustomers.length === 0) return;

    setIsImporting(true);
    setImportProgress({ current: 0, total: importedCustomers.length });

    try {
      const response = await fetch(`${API_URL}/api/customer-import/import?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(importedCustomers)
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        // Map imported customers to frontend format
        const mappedCustomers = data.customers.map(c => ({
          ...c,
          totalOrder: c.totalOrder || c.spent || c.payment || 0,
          avgOrder: c.avgOrder || c.payment || 0,
          orders: c.orders || (c.payment ? 1 : 0),
          initials: c.name ? c.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) : 'CU',
          odId: c.id?.substring(0, 5) || String(70000 + Math.floor(Math.random() * 10000)),
          lastOrder: c.date || new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })
        }));
        
        setCustomers(prev => [...mappedCustomers, ...prev]);
        toast.success(`Successfully imported ${data.count} customer(s)`);
        setShowImportModal(false);
        setImportedCustomers([]);
      } else {
        toast.error(data.detail || 'Failed to import customers');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import customers');
    } finally {
      setIsImporting(false);
      setImportProgress({ current: 0, total: 0 });
    }
  };

  // Remove a customer from import preview
  const removeFromImport = (index) => {
    setImportedCustomers(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and drop handler for clients
  const handleClientDragEnd = (result) => {
    if (!result.destination) return;
    
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    
    if (sourceIndex === destIndex) return;
    
    const reordered = Array.from(clients);
    const [removed] = reordered.splice(sourceIndex, 1);
    reordered.splice(destIndex, 0, removed);
    
    setClients(reordered);
    
    // Save the new order to backend
    saveClientOrder(reordered.map(c => c.id));
  };

  // Drag and drop handler for customers
  const handleCustomerDragEnd = (result) => {
    if (!result.destination) return;
    
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    
    if (sourceIndex === destIndex) return;
    
    const reordered = Array.from(customers);
    const [removed] = reordered.splice(sourceIndex, 1);
    reordered.splice(destIndex, 0, removed);
    
    setCustomers(reordered);
    
    // Save the new order to backend
    saveCustomerOrder(reordered.map(c => c.id));
  };

  // Save client order to backend (fire and forget)
  const saveClientOrder = async (orderIds) => {
    if (!userId) return;
    try {
      await fetch(`${API_URL}/api/client-list/reorder?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: orderIds })
      });
    } catch (error) {
      console.error('Failed to save client order:', error);
    }
  };

  // Save customer order to backend (fire and forget)
  const saveCustomerOrder = async (orderIds) => {
    if (!userId) return;
    try {
      await fetch(`${API_URL}/api/customer-list/reorder?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: orderIds })
      });
    } catch (error) {
      console.error('Failed to save customer order:', error);
    }
  };

  const handleDeleteClient = async () => {
    if (!itemToDelete || !userId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/client-list/clients/${itemToDelete.id}?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setClients(prev => prev.filter(c => c.id !== itemToDelete.id));
        toast.success(`${itemToDelete.name} deleted successfully`);
      } else {
        const err = await response.text();
        console.error('Failed to delete client:', err);
        toast.error('Failed to delete company');
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Failed to delete company');
    }
    
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDeleteCustomer = async () => {
    if (!itemToDelete || !userId) return;
    
    try {
      const response = await fetch(`${API_URL}/api/customer-list/customers/${itemToDelete.id}?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setCustomers(prev => prev.filter(c => c.id !== itemToDelete.id));
        toast.success(`${itemToDelete.name} deleted successfully`);
      } else {
        const err = await response.text();
        console.error('Failed to delete customer:', err);
        toast.error('Failed to delete customer');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      toast.error('Failed to delete customer');
    }
    
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const openEditClientModal = (client) => {
    setEditItem(client);
    setNewClient({
      name: client.name,
      type: client.type,
      projects: client.projects,
      tasks: client.tasks,
      employees: client.employees,
      budget: client.budget,
      color: client.color,
      image: client.image,
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      category: client.category || ''
    });
    setShowAddModal(true);
    setActiveDropdown(null);
  };

  const openEditCustomerModal = (customer) => {
    setEditItem(customer);
    setNewCustomer({
      name: customer.name,
      firstName: customer.first_name || customer.firstName || '',
      lastName: customer.last_name || customer.lastName || '',
      status: customer.status,
      totalOrder: customer.totalOrder,
      avgOrder: customer.avgOrder,
      orders: customer.orders,
      avatar: customer.avatar,
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      productType: customer.product_type || customer.productType || '',
      productName: customer.product_name || customer.productName || '',
      date: customer.date || '',
      payment: customer.payment || 0,
      bookingUrl: customer.booking_url || customer.bookingUrl || '',
      category: customer.category || ''
    });
    setShowAddCustomerModal(true);
    setActiveDropdown(null);
  };

  const openDetailsModal = (item, type) => {
    setSelectedItem({ ...item, itemType: type });
    setShowDetailsModal(true);
    setActiveDropdown(null);
  };

  const openDeleteModal = (item, type) => {
    setItemToDelete({ ...item, itemType: type });
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const handleSaveDetails = async () => {
    if (!selectedItem || !userId) return;
    
    try {
      if (selectedItem.itemType === 'client') {
        const response = await fetch(`${API_URL}/api/client-list/clients/${selectedItem.id}?user_id=${encodeURIComponent(userId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: selectedItem.name,
            subtitle: selectedItem.type,
            type: selectedItem.type,
            logo: selectedItem.image || selectedItem.logo,
            image: selectedItem.image,
            email: selectedItem.email,
            phone: selectedItem.phone,
            address: selectedItem.address,
            projects: selectedItem.projects,
            tasks: selectedItem.tasks,
            employees: selectedItem.employees,
            budget: selectedItem.budget,
            revenue: selectedItem.budget,
            color: selectedItem.color
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          const clientResponse = data.client;
          // Map API response to frontend expected structure
          const mappedClient = {
            ...clientResponse,
            type: clientResponse.subtitle || clientResponse.type || '',
            budget: clientResponse.budget || clientResponse.revenue || 0,
            projects: clientResponse.projects || 0,
            tasks: clientResponse.tasks || 0,
            employees: clientResponse.employees || 0,
            logo: clientResponse.logo || generateLogo(clientResponse.name),
            color: clientResponse.color || selectedItem.color || 'bg-purple-600'
          };
          setClients(prev => prev.map(c => c.id === selectedItem.id ? mappedClient : c));
          toast.success('Company details updated');
        } else {
          toast.error('Failed to update company');
          return;
        }
      } else {
        const response = await fetch(`${API_URL}/api/customer-list/customers/${selectedItem.id}?user_id=${encodeURIComponent(userId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: selectedItem.name,
            email: selectedItem.email,
            phone: selectedItem.phone,
            avatar: selectedItem.avatar,
            address: selectedItem.address,
            totalOrder: selectedItem.totalOrder,
            avgOrder: selectedItem.avgOrder,
            orders: selectedItem.orders,
            spent: selectedItem.totalOrder,
            purchases: selectedItem.orders,
            status: selectedItem.status
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          const customerResponse = data.customer;
          // Map API response to frontend expected structure
          const mappedCustomer = {
            ...customerResponse,
            totalOrder: customerResponse.totalOrder || customerResponse.spent || 0,
            avgOrder: customerResponse.avgOrder || 0,
            orders: customerResponse.orders || customerResponse.purchases || 0,
            initials: generateInitials(customerResponse.name),
            odId: selectedItem.odId || customerResponse.id?.substring(0, 5) || String(70000 + Math.floor(Math.random() * 10000)),
            lastOrder: selectedItem.lastOrder || customerResponse.member_since || 'N/A'
          };
          setCustomers(prev => prev.map(c => c.id === selectedItem.id ? mappedCustomer : c));
          toast.success('Customer details updated');
        } else {
          toast.error('Failed to update customer');
          return;
        }
      }
    } catch (error) {
      console.error('Error saving details:', error);
      toast.error('Failed to save details');
      return;
    }
    
    setShowDetailsModal(false);
    setSelectedItem(null);
  };

  // Filter and sort clients
  const getFilteredClients = () => {
    let filtered = clients.filter(c => 
      c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.type.toLowerCase().includes(searchTerm.toLowerCase())
    );

    switch (clientFilter) {
      case 'A-Z':
        return filtered.sort((a, b) => a.name.localeCompare(b.name));
      case 'Budget':
        return filtered.sort((a, b) => b.budget - a.budget);
      case 'Projects':
        return filtered.sort((a, b) => b.projects - a.projects);
      case 'Tasks':
        return filtered.sort((a, b) => b.tasks - a.tasks);
      default:
        return filtered;
    }
  };

  // Filter customers
  const getFilteredCustomers = () => {
    let filtered = customers.filter(c => 
      c.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
      c.odId.toLowerCase().includes(customerSearchTerm.toLowerCase())
    );

    if (customerFilter === 'Active') {
      filtered = filtered.filter(c => c.status === 'Active');
    } else if (customerFilter === 'Inactive') {
      filtered = filtered.filter(c => c.status === 'Inactive');
    }

    return filtered;
  };

  const filteredClients = getFilteredClients();
  const filteredCustomers = getFilteredCustomers();

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6 overflow-y-auto">
      {/* Hidden file input for card-level avatar uploads */}
      <input
        ref={cardPhotoInputRef}
        type="file"
        accept="image/*"
        onChange={handleCardPhotoUpload}
        className="hidden"
        data-testid="card-photo-input"
      />
      <div className="mb-4 md:mb-6">
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <h1 className="text-xl md:text-3xl font-bold">Clients & Customers</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-4 md:mb-6 flex items-center gap-3 md:gap-4 border-b border-white/20">
        {['Clients', 'Customers'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            data-testid={`tab-${tab.toLowerCase()}`}
            className={`px-4 md:px-6 py-2 md:py-3 text-xs md:text-sm font-semibold transition-all relative ${
              activeTab === tab
                ? 'text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {tab}
            {activeTab === tab && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500"></div>
            )}
          </button>
        ))}
      </div>

      {/* Clients Tab Content */}
      {activeTab === 'Clients' && (
        <div className="space-y-4 md:space-y-6">
          {/* Client KPI Banner */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-6">
            <div className="bg-black border border-white/40 rounded-xl md:rounded-2xl p-4 md:p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="text-xs md:text-sm text-gray-400">Total Clients</div>
                <Building2 size={16} className="text-purple-400 md:hidden" />
                <Building2 size={20} className="text-purple-400 hidden md:block" />
              </div>
              <div className="text-xl md:text-3xl font-bold mb-1 md:mb-2">{clients.length}</div>
              <div className="flex items-center gap-1 text-gray-500 text-xs md:text-sm">
                <span>{clients.length > 0 ? 'Total' : 'No data'}</span>
              </div>
            </div>

            <div className="bg-black border border-white/40 rounded-xl md:rounded-2xl p-4 md:p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="text-xs md:text-sm text-gray-400">Active</div>
                <Building2 size={16} className="text-blue-400 md:hidden" />
                <Building2 size={20} className="text-blue-400 hidden md:block" />
              </div>
              <div className="text-xl md:text-3xl font-bold mb-1 md:mb-2">{clients.filter(c => c.status === 'Active').length}</div>
              <div className="flex items-center gap-1 text-gray-500 text-xs md:text-sm">
                <span>{clients.length > 0 ? 'Active clients' : 'No data'}</span>
              </div>
            </div>

            <div className="bg-black border border-white/40 rounded-xl md:rounded-2xl p-4 md:p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="text-xs md:text-sm text-gray-400">Total Projects</div>
                <Briefcase size={16} className="text-green-400 md:hidden" />
                <Briefcase size={20} className="text-green-400 hidden md:block" />
              </div>
              <div className="text-xl md:text-3xl font-bold mb-1 md:mb-2">
                {clients.reduce((a, c) => a + (c.projects || 0), 0)}
              </div>
              <div className="flex items-center gap-1 text-gray-500 text-xs md:text-sm">
                <span>{clients.length > 0 ? 'All projects' : 'No data'}</span>
              </div>
            </div>

            <div className="bg-black border border-white/40 rounded-xl md:rounded-2xl p-4 md:p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="text-xs md:text-sm text-gray-400">Total Revenue</div>
                <DollarSign size={16} className="text-yellow-400 md:hidden" />
                <DollarSign size={20} className="text-yellow-400 hidden md:block" />
              </div>
              <div className="text-xl md:text-3xl font-bold mb-1 md:mb-2">
                {clients.length > 0 ? `$${Math.round(clients.reduce((a, c) => a + (c.budget || c.revenue || 0), 0) / 1000)}K` : '$0K'}
              </div>
              <div className="flex items-center gap-1 text-gray-500 text-xs md:text-sm">
                <span>{clients.length > 0 ? 'All clients' : 'No data'}</span>
              </div>
            </div>

            <div className="bg-black border border-white/40 rounded-xl md:rounded-2xl p-4 md:p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="text-xs md:text-sm text-gray-400">Inactive</div>
                <TrendingDown size={16} className="text-red-400 md:hidden" />
                <TrendingDown size={20} className="text-red-400 hidden md:block" />
              </div>
              <div className="text-xl md:text-3xl font-bold mb-1 md:mb-2">{clients.filter(c => c.status === 'Inactive').length}</div>
              <div className="flex items-center gap-1 text-gray-500 text-xs md:text-sm">
                <span>{clients.length > 0 ? 'Inactive clients' : 'No data'}</span>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
            {/* Filter Buttons */}
            <div className="flex items-center gap-2 md:gap-3 overflow-x-auto pb-2 md:pb-0">
              {customClientFilters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => setClientFilter(filter.id)}
                  data-testid={`filter-${filter.id.toLowerCase()}`}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${
                    clientFilter === filter.id
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : 'bg-black border border-white/40 hover:border-white/60'
                  }`}
                >
                  <span>{filter.icon}</span>
                  {filter.name}
                </button>
              ))}
              {/* Edit Categories Button */}
              <button
                onClick={() => setShowClientFilterModal(true)}
                data-testid="edit-client-categories-btn"
                className="px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1"
                title="Edit Categories"
              >
                <Pencil size={14} />
                Edit
              </button>
            </div>

            {/* Search and Actions */}
            <div className="flex items-center gap-2 md:gap-3">
              <button 
                onClick={() => setViewMode('grid')}
                data-testid="view-grid-btn"
                className={`hidden md:block p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-purple-600' : 'bg-black border border-white/40 hover:border-white/60 hover:shadow-[0_0_10px_rgba(255,255,255,0.3)]'}`}
              >
                <Grid size={18} />
              </button>
              <button 
                onClick={() => setViewMode('list')}
                data-testid="view-list-btn"
                className={`hidden md:block p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-purple-600' : 'bg-black border border-white/40 hover:border-white/60 hover:shadow-[0_0_10px_rgba(255,255,255,0.3)]'}`}
              >
                <List size={18} />
              </button>
              <div className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-black border border-white/40 rounded-lg flex-1 md:flex-none md:w-80 focus-within:border-purple-500 transition-all">
                <Search size={16} className="text-gray-400" />
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-clients"
                  className="bg-transparent border-none outline-none text-sm w-full"
                />
                {searchTerm && (
                  <button onClick={() => setSearchTerm('')} className="text-gray-400 hover:text-white">
                    <X size={14} />
                  </button>
                )}
                <span className="hidden md:block text-xs text-gray-500">⌘K</span>
              </div>
              <button 
                onClick={() => {
                  setEditItem(null);
                  setNewClient({ name: '', type: '', projects: 0, tasks: 0, employees: 0, budget: 0, color: 'bg-purple-600', image: null, email: '', phone: '', address: '', category: '' });
                  setShowAddModal(true);
                }}
                data-testid="add-company-btn"
                className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap"
              >
                <Plus size={16} />
                <span className="hidden sm:inline">Add Company</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
          </div>

          {/* Company Cards Grid */}
          <DragDropContext onDragEnd={handleClientDragEnd}>
            <Droppable droppableId="clients-list" direction={viewMode === 'grid' ? 'horizontal' : 'vertical'}>
              {(provided) => (
                <div 
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6' : 'space-y-3'}
                >
                  {filteredClients.map((client, index) => (
                    <Draggable key={client.id} draggableId={client.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          data-testid={`client-card-${client.id}`}
                          onClick={() => openDetailsModal(client, 'client')}
                          className={`bg-black border border-white/40 rounded-xl md:rounded-2xl p-4 md:p-6 hover:border-purple-500/60 hover:shadow-[0_0_20px_rgba(168,85,247,0.2)] transition-all duration-300 cursor-pointer ${viewMode === 'list' ? 'flex items-center gap-4' : ''} ${snapshot.isDragging ? 'shadow-2xl border-purple-500 z-50' : ''}`}
                        >
                          <div className={`flex items-center justify-between ${viewMode === 'list' ? 'flex-1' : 'mb-3 md:mb-4'}`}>
                            <div className="flex items-center gap-2 md:gap-3 min-w-0">
                              {/* Drag Handle */}
                              <div 
                                {...provided.dragHandleProps}
                                className="p-1 hover:bg-white/10 rounded cursor-grab active:cursor-grabbing flex-shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <GripVertical size={16} className="text-gray-500" />
                              </div>
                              {/* Client avatar with camera hover overlay */}
                              <div
                                className="relative w-10 md:w-12 h-10 md:h-12 flex-shrink-0 group cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCardUploadTarget({ id: client.id, type: 'client' });
                                  cardPhotoInputRef.current?.click();
                                }}
                                data-testid={`client-avatar-${client.id}`}
                              >
                                {client.image ? (
                                  <img src={client.image} alt={client.name} className="w-full h-full rounded-lg object-cover" />
                                ) : (
                                  <div className={`w-full h-full ${client.color} rounded-lg flex items-center justify-center text-white font-bold text-sm md:text-lg`}>
                                    {client.logo}
                                  </div>
                                )}
                                <div className="absolute inset-0 rounded-lg bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  {uploadingCardId === client.id ? (
                                    <Loader2 size={14} className="animate-spin text-white" />
                                  ) : (
                                    <Camera size={14} className="text-white" />
                                  )}
                                </div>
                              </div>
                              <div className="min-w-0">
                                <div className="font-semibold text-sm md:text-base truncate">{client.name}</div>
                                <div className="text-[10px] md:text-xs text-gray-400 truncate">{client.type}</div>
                              </div>
                            </div>
                            <div className="relative" ref={activeDropdown === `client-${client.id}` ? dropdownRef : null}>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveDropdown(activeDropdown === `client-${client.id}` ? null : `client-${client.id}`);
                                }}
                                data-testid={`client-menu-${client.id}`}
                                className="p-1 hover:bg-white/10 rounded transition-all flex-shrink-0"
                              >
                                <MoreVertical size={16} />
                              </button>
                              {activeDropdown === `client-${client.id}` && (
                                <div className="absolute right-0 top-8 bg-gray-900 border border-white/20 rounded-lg shadow-xl z-50 min-w-[140px] overflow-hidden">
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); openEditClientModal(client); }}
                                    data-testid={`edit-client-${client.id}`}
                                    className="w-full px-4 py-2.5 text-sm text-left hover:bg-white/10 flex items-center gap-2 transition-colors"
                                  >
                                    <Edit size={14} />
                                    Edit
                                  </button>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); openDetailsModal(client, 'client'); }}
                                    data-testid={`view-client-${client.id}`}
                                    className="w-full px-4 py-2.5 text-sm text-left hover:bg-white/10 flex items-center gap-2 transition-colors"
                                  >
                                    <Eye size={14} />
                                    View Details
                                  </button>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); openDeleteModal(client, 'client'); }}
                                    data-testid={`delete-client-${client.id}`}
                                    className="w-full px-4 py-2.5 text-sm text-left hover:bg-red-500/20 text-red-400 flex items-center gap-2 transition-colors"
                                  >
                                    <Trash2 size={14} />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          {viewMode === 'grid' && (
                            <div className="space-y-2 md:space-y-3">
                              <div className="flex items-center justify-between text-xs md:text-sm">
                                <div className="flex items-center gap-1.5 md:gap-2 text-gray-400">
                                  <Briefcase size={12} className="md:hidden" />
                                  <Briefcase size={14} className="hidden md:block" />
                                  <span>Open Projects</span>
                                </div>
                                <span className="font-semibold">{client.projects}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs md:text-sm">
                                <div className="flex items-center gap-1.5 md:gap-2 text-gray-400">
                                  <CheckCircle size={12} className="md:hidden" />
                                  <CheckCircle size={14} className="hidden md:block" />
                                  <span>Open Tasks</span>
                                </div>
                                <span className="font-semibold">{client.tasks}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs md:text-sm">
                                <div className="flex items-center gap-1.5 md:gap-2 text-gray-400">
                                  <Users size={12} className="md:hidden" />
                                  <Users size={14} className="hidden md:block" />
                                  <span>Employees</span>
                                </div>
                                <span className="font-semibold">{client.employees}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs md:text-sm">
                                <div className="flex items-center gap-1.5 md:gap-2 text-gray-400">
                                  <DollarSign size={12} className="md:hidden" />
                                  <DollarSign size={14} className="hidden md:block" />
                                  <span>Budget</span>
                                </div>
                                <span className="font-semibold">${client.budget.toLocaleString()}</span>
                              </div>
                            </div>
                          )}

                          {viewMode === 'list' && (
                            <div className="flex items-center gap-8 text-sm">
                              <div><span className="text-gray-400">Projects:</span> <span className="font-semibold">{client.projects}</span></div>
                              <div><span className="text-gray-400">Tasks:</span> <span className="font-semibold">{client.tasks}</span></div>
                              <div><span className="text-gray-400">Employees:</span> <span className="font-semibold">{client.employees}</span></div>
                              <div><span className="text-gray-400">Budget:</span> <span className="font-semibold">${client.budget.toLocaleString()}</span></div>
                            </div>
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {filteredClients.length === 0 && (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No companies found</p>
              <button 
                onClick={() => setShowAddModal(true)}
                className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm transition-all"
              >
                Add Your First Company
              </button>
            </div>
          )}

          {/* Pagination */}
          {filteredClients.length > 0 && (
            <div className="flex items-center justify-center gap-1.5 md:gap-2">
              <button className="p-1.5 md:p-2 bg-black border border-white/40 hover:border-white/60 rounded-lg transition-all">
                <ChevronRight size={14} className="rotate-180" />
              </button>
              <button className="px-3 md:px-4 py-1.5 md:py-2 bg-purple-600 rounded-lg text-xs md:text-sm font-medium">1</button>
              <button className="px-3 md:px-4 py-1.5 md:py-2 bg-black border border-white/40 hover:border-white/60 rounded-lg text-xs md:text-sm transition-all">2</button>
              <button className="hidden sm:block px-4 py-2 bg-black border border-white/40 hover:border-white/60 rounded-lg text-sm transition-all">3</button>
              <button className="p-1.5 md:p-2 bg-black border border-white/40 hover:border-white/60 rounded-lg transition-all">
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Customers Tab Content */}
      {activeTab === 'Customers' && (
        <div className="space-y-4 md:space-y-6">
          {/* KPI Banner */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-6">
            <div className="bg-black border border-white/40 rounded-xl md:rounded-2xl p-4 md:p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="text-xs md:text-sm text-gray-400">Total Customer</div>
                <Users size={16} className="text-purple-400 md:hidden" />
                <Users size={20} className="text-purple-400 hidden md:block" />
              </div>
              <div className="text-xl md:text-3xl font-bold mb-1 md:mb-2">{customers.length}</div>
              <div className="flex items-center gap-1 text-gray-500 text-xs md:text-sm">
                <span>{customers.length > 0 ? 'Total' : 'No data'}</span>
              </div>
            </div>

            <div className="bg-black border border-white/40 rounded-xl md:rounded-2xl p-4 md:p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="text-xs md:text-sm text-gray-400">Active</div>
                <Users size={16} className="text-blue-400 md:hidden" />
                <Users size={20} className="text-blue-400 hidden md:block" />
              </div>
              <div className="text-xl md:text-3xl font-bold mb-1 md:mb-2">{customers.filter(c => c.status === 'Active').length}</div>
              <div className="flex items-center gap-1 text-gray-500 text-xs md:text-sm">
                <span>{customers.length > 0 ? 'Active customers' : 'No data'}</span>
              </div>
            </div>

            <div className="bg-black border border-white/40 rounded-xl md:rounded-2xl p-4 md:p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="text-xs md:text-sm text-gray-400">Return Rate</div>
                <TrendingUp size={16} className="text-green-400 md:hidden" />
                <TrendingUp size={20} className="text-green-400 hidden md:block" />
              </div>
              <div className="text-xl md:text-3xl font-bold mb-1 md:mb-2">
                {customers.length > 0 ? `${Math.round(customers.filter(c => c.orders > 1).length / customers.length * 100)}%` : '0%'}
              </div>
              <div className="flex items-center gap-1 text-gray-500 text-xs md:text-sm">
                <span>{customers.length > 0 ? 'Repeat buyers' : 'No data'}</span>
              </div>
            </div>

            <div className="bg-black border border-white/40 rounded-xl md:rounded-2xl p-4 md:p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="text-xs md:text-sm text-gray-400">Total Revenue</div>
                <DollarSign size={16} className="text-yellow-400 md:hidden" />
                <DollarSign size={20} className="text-yellow-400 hidden md:block" />
              </div>
              <div className="text-xl md:text-3xl font-bold mb-1 md:mb-2">
                {customers.length > 0 ? `$${Math.round(customers.reduce((a, c) => a + (c.totalOrder || c.spent || 0), 0) / 1000)}K` : '$0K'}
              </div>
              <div className="flex items-center gap-1 text-gray-500 text-xs md:text-sm">
                <span>{customers.length > 0 ? 'All customers' : 'No data'}</span>
              </div>
            </div>

            <div className="bg-black border border-white/40 rounded-xl md:rounded-2xl p-4 md:p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="text-xs md:text-sm text-gray-400">Inactive</div>
                <TrendingDown size={16} className="text-red-400 md:hidden" />
                <TrendingDown size={20} className="text-red-400 hidden md:block" />
              </div>
              <div className="text-xl md:text-3xl font-bold mb-1 md:mb-2">{customers.filter(c => c.status === 'Inactive').length}</div>
              <div className="flex items-center gap-1 text-gray-500 text-xs md:text-sm">
                <span>{customers.length > 0 ? 'Inactive customers' : 'No data'}</span>
              </div>
            </div>
          </div>

          {/* Filters and Controls */}
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
            <div className="flex items-center gap-2 md:gap-3 overflow-x-auto pb-2 md:pb-0">
              {customCustomerFilters.map((filter) => (
                <button 
                  key={filter.id}
                  onClick={() => setCustomerFilter(filter.id)}
                  data-testid={`filter-${filter.id.toLowerCase()}-customers`}
                  className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${customerFilter === filter.id ? 'bg-purple-600 hover:bg-purple-700' : 'bg-black border border-white/40 hover:border-white/60'}`}
                >
                  <span>{filter.icon}</span>
                  {filter.name}
                </button>
              ))}
              {/* Edit Categories Button */}
              <button
                onClick={() => setShowCustomerFilterModal(true)}
                data-testid="edit-customer-categories-btn"
                className="px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1"
                title="Edit Categories"
              >
                <Pencil size={14} />
                Edit
              </button>
            </div>

            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <div className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-black border border-white/40 rounded-lg flex-1 md:flex-none focus-within:border-purple-500 transition-all">
                <Search size={16} className="text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  data-testid="search-customers"
                  className="bg-transparent border-none outline-none text-sm w-full md:w-40"
                />
                {customerSearchTerm && (
                  <button onClick={() => setCustomerSearchTerm('')} className="text-gray-400 hover:text-white">
                    <X size={14} />
                  </button>
                )}
              </div>
              {/* Import File Button */}
              <input
                type="file"
                ref={importFileInputRef}
                accept=".csv,.xlsx,.xls,.pdf"
                onChange={handleFileUpload}
                className="hidden"
              />
              <button 
                onClick={() => importFileInputRef.current?.click()}
                disabled={isImporting}
                data-testid="import-customers-btn"
                className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-black border border-white/40 hover:border-purple-500 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap"
              >
                {isImporting ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                <span className="hidden sm:inline">{isImporting ? 'Processing...' : 'Import File'}</span>
                <span className="sm:hidden">{isImporting ? '...' : 'Import'}</span>
              </button>
              <button 
                onClick={() => {
                  setEditItem(null);
                  setNewCustomer({ name: '', firstName: '', lastName: '', status: 'Active', totalOrder: 0, avgOrder: 0, orders: 0, avatar: null, email: '', phone: '', address: '', productType: '', productName: '', date: '', payment: 0, bookingUrl: '', category: '' });
                  setShowAddCustomerModal(true);
                }}
                data-testid="add-customer-btn"
                className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap"
              >
                <Plus size={16} />
                <span className="hidden sm:inline">Add Customer</span>
                <span className="sm:hidden">Add</span>
              </button>
            </div>
          </div>

          {/* Customer Cards Grid */}
          <DragDropContext onDragEnd={handleCustomerDragEnd}>
            <Droppable droppableId="customers-list" direction="horizontal">
              {(provided) => (
                <div 
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6"
                >
                  {filteredCustomers.map((customer, index) => (
                    <Draggable key={customer.id} draggableId={customer.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          data-testid={`customer-card-${customer.id}`}
                          onClick={() => openDetailsModal(customer, 'customer')}
                          className={`bg-black border border-white/40 rounded-xl md:rounded-2xl p-4 md:p-6 hover:border-purple-500/60 hover:shadow-[0_0_20px_rgba(168,85,247,0.2)] transition-all duration-300 cursor-pointer ${snapshot.isDragging ? 'shadow-2xl border-purple-500 z-50' : ''}`}
                        >
                          <div className="flex items-center justify-between mb-3 md:mb-4">
                            <div className="flex items-center gap-2 md:gap-3 min-w-0">
                              {/* Drag Handle */}
                              <div 
                                {...provided.dragHandleProps}
                                className="p-1 hover:bg-white/10 rounded cursor-grab active:cursor-grabbing flex-shrink-0"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <GripVertical size={16} className="text-gray-500" />
                              </div>
                              {/* Customer avatar with camera hover overlay */}
                              <div
                                className="relative w-10 md:w-12 h-10 md:h-12 flex-shrink-0 group cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCardUploadTarget({ id: customer.id, type: 'customer' });
                                  cardPhotoInputRef.current?.click();
                                }}
                                data-testid={`customer-avatar-${customer.id}`}
                              >
                                {customer.avatar ? (
                                  <img src={customer.avatar} alt={customer.name} className="w-full h-full rounded-full object-cover" />
                                ) : (
                                  <div className="w-full h-full bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-sm md:text-base">
                                    {customer.initials}
                                  </div>
                                )}
                                <div className="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  {uploadingCardId === customer.id ? (
                                    <Loader2 size={14} className="animate-spin text-white" />
                                  ) : (
                                    <Camera size={14} className="text-white" />
                                  )}
                                </div>
                              </div>
                              <div className="min-w-0">
                                <div className="font-semibold text-sm md:text-base truncate">{customer.name}</div>
                                <div className="text-[10px] md:text-xs text-gray-400">ID #{customer.odId}</div>
                              </div>
                            </div>
                            <div className="relative" ref={activeDropdown === `customer-${customer.id}` ? dropdownRef : null}>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveDropdown(activeDropdown === `customer-${customer.id}` ? null : `customer-${customer.id}`);
                                }}
                                data-testid={`customer-menu-${customer.id}`}
                                className="p-1 hover:bg-white/10 rounded transition-all flex-shrink-0"
                              >
                                <MoreVertical size={16} />
                              </button>
                              {activeDropdown === `customer-${customer.id}` && (
                                <div className="absolute right-0 top-8 bg-gray-900 border border-white/20 rounded-lg shadow-xl z-50 min-w-[140px] overflow-hidden">
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); openEditCustomerModal(customer); }}
                                    data-testid={`edit-customer-${customer.id}`}
                                    className="w-full px-4 py-2.5 text-sm text-left hover:bg-white/10 flex items-center gap-2 transition-colors"
                                  >
                                    <Edit size={14} />
                                    Edit
                                  </button>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); openDetailsModal(customer, 'customer'); }}
                                    data-testid={`view-customer-${customer.id}`}
                                    className="w-full px-4 py-2.5 text-sm text-left hover:bg-white/10 flex items-center gap-2 transition-colors"
                                  >
                                    <Eye size={14} />
                                    View Details
                                  </button>
                                  <button 
                                    onClick={(e) => { e.stopPropagation(); openDeleteModal(customer, 'customer'); }}
                                    data-testid={`delete-customer-${customer.id}`}
                                    className="w-full px-4 py-2.5 text-sm text-left hover:bg-red-500/20 text-red-400 flex items-center gap-2 transition-colors"
                                  >
                                    <Trash2 size={14} />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="mb-3 md:mb-4">
                            <span className={`px-2 md:px-3 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs font-medium ${
                              customer.status === 'Active'
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {customer.status}
                            </span>
                          </div>

                          {/* Customer Details - Name, Product, Booking */}
                          <div className="space-y-1.5 md:space-y-2 mb-3 md:mb-4 text-xs md:text-sm">
                            {(customer.first_name || customer.last_name || customer.firstName || customer.lastName) && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-400">Name</span>
                                <span className="font-medium truncate max-w-[60%]">
                                  {customer.first_name || customer.firstName} {customer.last_name || customer.lastName}
                                </span>
                              </div>
                            )}
                            {(customer.product_name || customer.productName) && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-400">Product</span>
                                <span className="font-medium truncate max-w-[60%]">{customer.product_name || customer.productName}</span>
                              </div>
                            )}
                            {(customer.booking_url || customer.bookingUrl) && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-400">Booking</span>
                                <a 
                                  href={customer.booking_url || customer.bookingUrl} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-purple-400 hover:text-purple-300 truncate max-w-[60%] hover:underline"
                                >
                                  View Link
                                </a>
                              </div>
                            )}
                          </div>

                          <div className="space-y-1.5 md:space-y-2 mb-3 md:mb-4">
                            <div className="flex items-center justify-between text-xs md:text-sm">
                              <span className="text-gray-400">Total Order</span>
                              <div className="flex items-center gap-1">
                                <span className="font-semibold">${customer.totalOrder.toLocaleString()}</span>
                                <TrendingUp size={10} className="text-green-400" />
                              </div>
                            </div>
                            <div className="flex items-center justify-between text-xs md:text-sm">
                              <span className="text-gray-400">Avg. Order</span>
                              <div className="flex items-center gap-1">
                                <span className="font-semibold">${customer.avgOrder.toLocaleString()}</span>
                                <TrendingUp size={10} className="text-green-400" />
                              </div>
                            </div>
                            <div className="flex items-center justify-between text-xs md:text-sm">
                              <span className="text-gray-400">No. of Order</span>
                              <span className="font-semibold">{customer.orders}</span>
                            </div>
                          </div>

                          <div className="pt-3 md:pt-4 border-t border-white/10">
                            <div className="text-[10px] md:text-xs text-gray-400 mb-0.5 md:mb-1">Last Order:</div>
                            <div className="text-xs md:text-sm">{customer.lastOrder}</div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {filteredCustomers.length === 0 && (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No customers found</p>
              <button 
                onClick={() => setShowAddCustomerModal(true)}
                className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm transition-all"
              >
                Add Your First Customer
              </button>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Company Modal */}
      {showAddModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => { setShowAddModal(false); setEditItem(null); }}
        >
          <div 
            className="bg-gray-900 border border-white/20 rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-gray-900">
              <h2 className="text-lg font-semibold">{editItem ? 'Edit Company' : 'Add New Company'}</h2>
              <button 
                onClick={() => { setShowAddModal(false); setEditItem(null); }}
                className="p-1 hover:bg-white/10 rounded-lg transition-all"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Image Upload */}
              <div className="flex justify-center">
                <div className="relative">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'client')}
                    className="hidden"
                  />
                  {newClient.image ? (
                    <img src={newClient.image} alt="Company" className="w-20 h-20 rounded-xl object-cover" />
                  ) : (
                    <div className={`w-20 h-20 ${newClient.color} rounded-xl flex items-center justify-center text-white font-bold text-2xl`}>
                      {newClient.name ? generateLogo(newClient.name) : 'CO'}
                    </div>
                  )}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute -bottom-2 -right-2 p-2 bg-purple-600 rounded-full hover:bg-purple-700 transition-all"
                  >
                    <Camera size={14} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Company Name *</label>
                <input
                  type="text"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  data-testid="input-company-name"
                  placeholder="Enter company name"
                  className="w-full px-4 py-2.5 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Company Type *</label>
                <input
                  type="text"
                  value={newClient.type}
                  onChange={(e) => setNewClient({ ...newClient, type: e.target.value })}
                  data-testid="input-company-type"
                  placeholder="e.g., Real Estate Agency"
                  className="w-full px-4 py-2.5 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Category</label>
                <select
                  value={newClient.category}
                  onChange={(e) => setNewClient({ ...newClient, category: e.target.value })}
                  data-testid="input-client-category"
                  className="w-full px-4 py-2.5 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all"
                >
                  <option value="">Select a category...</option>
                  {customClientFilters.filter(f => !['A-Z', 'Budget', 'Projects', 'Tasks'].includes(f.id)).map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Custom categories can be added via Edit button above</p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Brand Color</label>
                <div className="flex flex-wrap gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => setNewClient({ ...newClient, color: color.value })}
                      className={`w-8 h-8 rounded-lg ${color.value} ${newClient.color === color.value ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : ''} transition-all`}
                      title={color.label}
                    />
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Projects</label>
                  <input
                    type="number"
                    value={newClient.projects}
                    onChange={(e) => setNewClient({ ...newClient, projects: e.target.value })}
                    data-testid="input-projects"
                    className="w-full px-4 py-2.5 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Tasks</label>
                  <input
                    type="number"
                    value={newClient.tasks}
                    onChange={(e) => setNewClient({ ...newClient, tasks: e.target.value })}
                    data-testid="input-tasks"
                    className="w-full px-4 py-2.5 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Employees</label>
                  <input
                    type="number"
                    value={newClient.employees}
                    onChange={(e) => setNewClient({ ...newClient, employees: e.target.value })}
                    data-testid="input-employees"
                    className="w-full px-4 py-2.5 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Budget ($)</label>
                  <input
                    type="number"
                    value={newClient.budget}
                    onChange={(e) => setNewClient({ ...newClient, budget: e.target.value })}
                    data-testid="input-budget"
                    className="w-full px-4 py-2.5 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Email</label>
                <input
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  placeholder="company@email.com"
                  className="w-full px-4 py-2.5 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Phone</label>
                <input
                  type="tel"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="w-full px-4 py-2.5 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Address</label>
                <input
                  type="text"
                  value={newClient.address}
                  onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                  placeholder="123 Business St, City, State"
                  className="w-full px-4 py-2.5 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-white/10 sticky bottom-0 bg-gray-900">
              <button
                onClick={() => { setShowAddModal(false); setEditItem(null); }}
                className="px-4 py-2 bg-black border border-white/20 hover:border-white/40 rounded-lg text-sm transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleAddClient}
                data-testid="save-company-btn"
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-all"
              >
                {editItem ? 'Save Changes' : 'Add Company'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Customer Modal */}
      {showAddCustomerModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => { setShowAddCustomerModal(false); setEditItem(null); }}
        >
          <div 
            className="bg-gray-900 border border-white/20 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-gray-900">
              <h2 className="text-lg font-semibold">{editItem ? 'Edit Customer' : 'Add New Customer'}</h2>
              <button 
                onClick={() => { setShowAddCustomerModal(false); setEditItem(null); }}
                className="p-1 hover:bg-white/10 rounded-lg transition-all"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Avatar Upload */}
              <div className="flex justify-center">
                <div className="relative">
                  <input
                    type="file"
                    ref={customerFileInputRef}
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'customer')}
                    className="hidden"
                  />
                  {newCustomer.avatar ? (
                    <img src={newCustomer.avatar} alt="Customer" className="w-20 h-20 rounded-full object-cover" />
                  ) : (
                    <div className="w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                      {newCustomer.name || (newCustomer.firstName && newCustomer.lastName) 
                        ? generateInitials(newCustomer.name || `${newCustomer.firstName} ${newCustomer.lastName}`) 
                        : 'CU'}
                    </div>
                  )}
                  <button
                    onClick={() => customerFileInputRef.current?.click()}
                    data-testid="upload-customer-avatar"
                    className="absolute -bottom-2 -right-2 p-2 bg-purple-600 rounded-full hover:bg-purple-700 transition-all"
                  >
                    <Camera size={14} />
                  </button>
                </div>
              </div>

              {/* Name Fields Section */}
              <div className="p-3 bg-black/50 rounded-lg space-y-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Customer Identity</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">First Name</label>
                    <input
                      type="text"
                      value={newCustomer.firstName}
                      onChange={(e) => setNewCustomer({ ...newCustomer, firstName: e.target.value })}
                      data-testid="input-first-name"
                      placeholder="John"
                      className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={newCustomer.lastName}
                      onChange={(e) => setNewCustomer({ ...newCustomer, lastName: e.target.value })}
                      data-testid="input-last-name"
                      placeholder="Doe"
                      className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Full Name (or auto-generated)</label>
                  <input
                    type="text"
                    value={newCustomer.name}
                    onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    data-testid="input-customer-name"
                    placeholder={newCustomer.firstName || newCustomer.lastName ? `${newCustomer.firstName} ${newCustomer.lastName}`.trim() : "Enter full name"}
                    className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                  />
                </div>
              </div>

              {/* Product/Transaction Section */}
              <div className="p-3 bg-black/50 rounded-lg space-y-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Product & Transaction</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Product Type</label>
                    <input
                      type="text"
                      value={newCustomer.productType}
                      onChange={(e) => setNewCustomer({ ...newCustomer, productType: e.target.value })}
                      data-testid="input-product-type"
                      placeholder="Service, Goods, etc."
                      className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Product Name</label>
                    <input
                      type="text"
                      value={newCustomer.productName}
                      onChange={(e) => setNewCustomer({ ...newCustomer, productName: e.target.value })}
                      data-testid="input-product-name"
                      placeholder="Product/Service name"
                      className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Date</label>
                    <input
                      type="text"
                      value={newCustomer.date}
                      onChange={(e) => setNewCustomer({ ...newCustomer, date: e.target.value })}
                      data-testid="input-date"
                      placeholder="MM/DD/YYYY"
                      className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Payment ($)</label>
                    <input
                      type="number"
                      value={newCustomer.payment}
                      onChange={(e) => setNewCustomer({ ...newCustomer, payment: e.target.value })}
                      data-testid="input-payment"
                      placeholder="0.00"
                      className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Booking URL</label>
                  <input
                    type="url"
                    value={newCustomer.bookingUrl}
                    onChange={(e) => setNewCustomer({ ...newCustomer, bookingUrl: e.target.value })}
                    data-testid="input-booking-url"
                    placeholder="https://..."
                    className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                  />
                </div>
              </div>

              {/* Status & Order Stats Section */}
              <div className="p-3 bg-black/50 rounded-lg space-y-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Status & Statistics</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Status</label>
                    <select
                      value={newCustomer.status}
                      onChange={(e) => setNewCustomer({ ...newCustomer, status: e.target.value })}
                      data-testid="input-customer-status"
                      className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Category</label>
                    <select
                      value={newCustomer.category}
                      onChange={(e) => setNewCustomer({ ...newCustomer, category: e.target.value })}
                      data-testid="input-customer-category"
                      className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                    >
                      <option value="">Select category...</option>
                      {customCustomerFilters.filter(f => !['All', 'Active', 'Inactive'].includes(f.id)).map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Total Orders ($)</label>
                    <input
                      type="number"
                      value={newCustomer.totalOrder}
                      onChange={(e) => setNewCustomer({ ...newCustomer, totalOrder: e.target.value })}
                      data-testid="input-total-order"
                      className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Avg. Order ($)</label>
                    <input
                      type="number"
                      value={newCustomer.avgOrder}
                      onChange={(e) => setNewCustomer({ ...newCustomer, avgOrder: e.target.value })}
                      data-testid="input-avg-order"
                      className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1"># of Orders</label>
                    <input
                      type="number"
                      value={newCustomer.orders}
                      onChange={(e) => setNewCustomer({ ...newCustomer, orders: e.target.value })}
                      data-testid="input-orders"
                      className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Section */}
              <div className="p-3 bg-black/50 rounded-lg space-y-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Contact Information</p>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Email</label>
                  <input
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    data-testid="input-customer-email"
                    placeholder="customer@email.com"
                    className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={newCustomer.phone}
                    onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    data-testid="input-customer-phone"
                    placeholder="(555) 123-4567"
                    className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-1">Address</label>
                  <input
                    type="text"
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                    data-testid="input-customer-address"
                    placeholder="123 Main St, City, State"
                    className="w-full px-3 py-2 bg-black border border-white/20 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-all text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-4 border-t border-white/10 sticky bottom-0 bg-gray-900">
              <button
                onClick={() => { setShowAddCustomerModal(false); setEditItem(null); }}
                className="px-4 py-2 bg-black border border-white/20 hover:border-white/40 rounded-lg text-sm transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCustomer}
                data-testid="save-customer-btn"
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-all"
              >
                {editItem ? 'Save Changes' : 'Add Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal (Click on Card) */}
      {showDetailsModal && selectedItem && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => { setShowDetailsModal(false); setSelectedItem(null); }}
        >
          <div 
            className="bg-gray-900 border border-white/20 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 bg-gray-900">
              <h2 className="text-lg font-semibold">
                {selectedItem.itemType === 'client' ? 'Company Details' : 'Customer Details'}
              </h2>
              <button 
                onClick={() => { setShowDetailsModal(false); setSelectedItem(null); }}
                className="p-1 hover:bg-white/10 rounded-lg transition-all"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6">
              {/* Header with Image/Avatar */}
              <div className="flex flex-col items-center mb-6">
                <div className="relative mb-4">
                  <input
                    type="file"
                    id="details-image-upload"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, selectedItem.itemType === 'client' ? 'details-client' : 'details-customer')}
                    className="hidden"
                  />
                  {selectedItem.itemType === 'client' ? (
                    selectedItem.image ? (
                      <img src={selectedItem.image} alt={selectedItem.name} className="w-24 h-24 rounded-xl object-cover" />
                    ) : (
                      <div className={`w-24 h-24 ${selectedItem.color} rounded-xl flex items-center justify-center text-white font-bold text-3xl`}>
                        {selectedItem.logo}
                      </div>
                    )
                  ) : (
                    selectedItem.avatar ? (
                      <img src={selectedItem.avatar} alt={selectedItem.name} className="w-24 h-24 rounded-full object-cover" />
                    ) : (
                      <div className="w-24 h-24 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-3xl">
                        {selectedItem.initials}
                      </div>
                    )
                  )}
                  <button
                    onClick={() => document.getElementById('details-image-upload')?.click()}
                    data-testid="change-details-image"
                    className="absolute -bottom-2 -right-2 p-2 bg-purple-600 rounded-full hover:bg-purple-700 transition-all"
                  >
                    <Camera size={14} />
                  </button>
                </div>
                
                <input
                  type="text"
                  value={selectedItem.name}
                  onChange={(e) => setSelectedItem({ ...selectedItem, name: e.target.value })}
                  className="text-xl font-bold text-center bg-transparent border-b border-transparent hover:border-white/20 focus:border-purple-500 outline-none px-2 py-1 transition-all"
                />
                
                {selectedItem.itemType === 'client' ? (
                  <input
                    type="text"
                    value={selectedItem.type}
                    onChange={(e) => setSelectedItem({ ...selectedItem, type: e.target.value })}
                    className="text-gray-400 text-sm text-center bg-transparent border-b border-transparent hover:border-white/20 focus:border-purple-500 outline-none px-2 py-1 mt-1 transition-all"
                  />
                ) : (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-gray-400 text-sm">ID #{selectedItem.odId}</span>
                    <select
                      value={selectedItem.status}
                      onChange={(e) => setSelectedItem({ ...selectedItem, status: e.target.value })}
                      className={`px-2 py-0.5 rounded-full text-xs font-medium bg-transparent border cursor-pointer ${
                        selectedItem.status === 'Active' ? 'border-green-500 text-green-400' : 'border-red-500 text-red-400'
                      }`}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Details Grid */}
              {selectedItem.itemType === 'client' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-black/50 rounded-lg p-3">
                      <div className="text-xs text-gray-400 mb-1">Projects</div>
                      <input
                        type="number"
                        value={selectedItem.projects}
                        onChange={(e) => setSelectedItem({ ...selectedItem, projects: Number(e.target.value) })}
                        className="text-lg font-semibold bg-transparent w-full outline-none"
                      />
                    </div>
                    <div className="bg-black/50 rounded-lg p-3">
                      <div className="text-xs text-gray-400 mb-1">Tasks</div>
                      <input
                        type="number"
                        value={selectedItem.tasks}
                        onChange={(e) => setSelectedItem({ ...selectedItem, tasks: Number(e.target.value) })}
                        className="text-lg font-semibold bg-transparent w-full outline-none"
                      />
                    </div>
                    <div className="bg-black/50 rounded-lg p-3">
                      <div className="text-xs text-gray-400 mb-1">Employees</div>
                      <input
                        type="number"
                        value={selectedItem.employees}
                        onChange={(e) => setSelectedItem({ ...selectedItem, employees: Number(e.target.value) })}
                        className="text-lg font-semibold bg-transparent w-full outline-none"
                      />
                    </div>
                    <div className="bg-black/50 rounded-lg p-3">
                      <div className="text-xs text-gray-400 mb-1">Budget</div>
                      <div className="flex items-center">
                        <span className="text-lg font-semibold">$</span>
                        <input
                          type="number"
                          value={selectedItem.budget}
                          onChange={(e) => setSelectedItem({ ...selectedItem, budget: Number(e.target.value) })}
                          className="text-lg font-semibold bg-transparent w-full outline-none ml-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-white/10">
                    <div>
                      <label className="text-xs text-gray-400">Email</label>
                      <input
                        type="email"
                        value={selectedItem.email || ''}
                        onChange={(e) => setSelectedItem({ ...selectedItem, email: e.target.value })}
                        placeholder="Add email..."
                        className="w-full bg-transparent border-b border-white/10 focus:border-purple-500 outline-none py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Phone</label>
                      <input
                        type="tel"
                        value={selectedItem.phone || ''}
                        onChange={(e) => setSelectedItem({ ...selectedItem, phone: e.target.value })}
                        placeholder="Add phone..."
                        className="w-full bg-transparent border-b border-white/10 focus:border-purple-500 outline-none py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Address</label>
                      <input
                        type="text"
                        value={selectedItem.address || ''}
                        onChange={(e) => setSelectedItem({ ...selectedItem, address: e.target.value })}
                        placeholder="Add address..."
                        className="w-full bg-transparent border-b border-white/10 focus:border-purple-500 outline-none py-1 text-sm"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-black/50 rounded-lg p-3">
                      <div className="text-xs text-gray-400 mb-1">Total Orders</div>
                      <div className="flex items-center">
                        <span className="text-lg font-semibold">$</span>
                        <input
                          type="number"
                          value={selectedItem.totalOrder}
                          onChange={(e) => setSelectedItem({ ...selectedItem, totalOrder: Number(e.target.value) })}
                          className="text-lg font-semibold bg-transparent w-full outline-none ml-1"
                        />
                      </div>
                    </div>
                    <div className="bg-black/50 rounded-lg p-3">
                      <div className="text-xs text-gray-400 mb-1">Avg. Order</div>
                      <div className="flex items-center">
                        <span className="text-lg font-semibold">$</span>
                        <input
                          type="number"
                          value={selectedItem.avgOrder}
                          onChange={(e) => setSelectedItem({ ...selectedItem, avgOrder: Number(e.target.value) })}
                          className="text-lg font-semibold bg-transparent w-full outline-none ml-1"
                        />
                      </div>
                    </div>
                    <div className="bg-black/50 rounded-lg p-3 col-span-2">
                      <div className="text-xs text-gray-400 mb-1">Number of Orders</div>
                      <input
                        type="number"
                        value={selectedItem.orders}
                        onChange={(e) => setSelectedItem({ ...selectedItem, orders: Number(e.target.value) })}
                        className="text-lg font-semibold bg-transparent w-full outline-none"
                      />
                    </div>
                  </div>

                  <div className="bg-black/50 rounded-lg p-3">
                    <div className="text-xs text-gray-400 mb-1">Last Order</div>
                    <div className="text-sm">{selectedItem.lastOrder}</div>
                  </div>

                  <div className="space-y-3 pt-4 border-t border-white/10">
                    <div>
                      <label className="text-xs text-gray-400">Email</label>
                      <input
                        type="email"
                        value={selectedItem.email || ''}
                        onChange={(e) => setSelectedItem({ ...selectedItem, email: e.target.value })}
                        placeholder="Add email..."
                        className="w-full bg-transparent border-b border-white/10 focus:border-purple-500 outline-none py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Phone</label>
                      <input
                        type="tel"
                        value={selectedItem.phone || ''}
                        onChange={(e) => setSelectedItem({ ...selectedItem, phone: e.target.value })}
                        placeholder="Add phone..."
                        className="w-full bg-transparent border-b border-white/10 focus:border-purple-500 outline-none py-1 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Address</label>
                      <input
                        type="text"
                        value={selectedItem.address || ''}
                        onChange={(e) => setSelectedItem({ ...selectedItem, address: e.target.value })}
                        placeholder="Add address..."
                        className="w-full bg-transparent border-b border-white/10 focus:border-purple-500 outline-none py-1 text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 p-4 border-t border-white/10 sticky bottom-0 bg-gray-900">
              <button
                onClick={() => openDeleteModal(selectedItem, selectedItem.itemType)}
                className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg text-sm transition-all flex items-center gap-2"
              >
                <Trash2 size={14} />
                Delete
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => { setShowDetailsModal(false); setSelectedItem(null); }}
                  className="px-4 py-2 bg-black border border-white/20 hover:border-white/40 rounded-lg text-sm transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveDetails}
                  data-testid="save-details-btn"
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-all"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && itemToDelete && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={() => { setShowDeleteModal(false); setItemToDelete(null); }}
        >
          <div 
            className="bg-gray-900 border border-white/20 rounded-2xl w-full max-w-sm overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 size={32} className="text-red-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Delete {itemToDelete.itemType === 'client' ? 'Company' : 'Customer'}?</h3>
              <p className="text-gray-400 text-sm mb-6">
                Are you sure you want to delete <span className="text-white font-medium">{itemToDelete.name}</span>? This action cannot be undone.
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => { setShowDeleteModal(false); setItemToDelete(null); }}
                  className="px-4 py-2 bg-black border border-white/20 hover:border-white/40 rounded-lg text-sm transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={itemToDelete.itemType === 'client' ? handleDeleteClient : handleDeleteCustomer}
                  data-testid="confirm-delete-btn"
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium transition-all"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Preview Modal */}
      {showImportModal && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={() => { setShowImportModal(false); setImportedCustomers([]); }}
        >
          <div 
            className="bg-gray-900 border border-white/20 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div>
                <h2 className="text-lg font-semibold">Import Preview</h2>
                <p className="text-sm text-gray-400">{importedCustomers.length} customer(s) found in file</p>
              </div>
              <button 
                onClick={() => { setShowImportModal(false); setImportedCustomers([]); }}
                className="p-1 hover:bg-white/10 rounded-lg transition-all"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {importedCustomers.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Users size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No customers to import</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {importedCustomers.map((customer, index) => (
                    <div key={index} className="bg-black/50 border border-white/10 rounded-lg p-3 flex items-center justify-between hover:border-purple-500/30 transition-all">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                          {(customer.first_name?.[0] || '') + (customer.last_name?.[0] || '') || 'CU'}
                        </div>
                        <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-4 gap-2">
                          <div>
                            <p className="text-xs text-gray-500">Name</p>
                            <p className="text-sm text-white truncate">{customer.first_name} {customer.last_name}</p>
                          </div>
                          {customer.product_type && (
                            <div>
                              <p className="text-xs text-gray-500">Product Type</p>
                              <p className="text-sm text-white truncate">{customer.product_type}</p>
                            </div>
                          )}
                          {customer.product_name && (
                            <div>
                              <p className="text-xs text-gray-500">Product Name</p>
                              <p className="text-sm text-white truncate">{customer.product_name}</p>
                            </div>
                          )}
                          {customer.payment && (
                            <div>
                              <p className="text-xs text-gray-500">Payment</p>
                              <p className="text-sm text-green-400">${customer.payment}</p>
                            </div>
                          )}
                          {customer.date && (
                            <div>
                              <p className="text-xs text-gray-500">Date</p>
                              <p className="text-sm text-white truncate">{customer.date}</p>
                            </div>
                          )}
                          {customer.booking_url && (
                            <div>
                              <p className="text-xs text-gray-500">Booking URL</p>
                              <a href={customer.booking_url} target="_blank" rel="noopener noreferrer" className="text-sm text-purple-400 hover:underline truncate block">{customer.booking_url}</a>
                            </div>
                          )}
                        </div>
                      </div>
                      <button 
                        onClick={() => removeFromImport(index)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all flex-shrink-0"
                        title="Remove from import"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 p-4 border-t border-white/10 bg-gray-900">
              <div className="text-sm text-gray-400">
                {importedCustomers.length} customer(s) will be imported
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => { setShowImportModal(false); setImportedCustomers([]); }}
                  className="px-4 py-2 bg-black border border-white/20 hover:border-white/40 rounded-lg text-sm transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportCustomers}
                  disabled={isImporting || importedCustomers.length === 0}
                  data-testid="confirm-import-btn"
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg text-sm font-medium transition-all flex items-center gap-2"
                >
                  {isImporting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      Import All
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client Filter Categories Modal */}
      {showClientFilterModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowClientFilterModal(false)}>
          <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Edit Client Filters</h3>
              <button onClick={() => setShowClientFilterModal(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="p-4 border-b border-gray-700">
              <div className="flex gap-2 mb-3">
                <div className="relative">
                  <button className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center" onClick={(e) => e.currentTarget.nextElementSibling.classList.toggle('hidden')}>{newFilterIcon}</button>
                  <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-5 gap-1 w-40">
                    {filterIcons.map(icon => (<button key={icon} onClick={(e) => { setNewFilterIcon(icon); e.currentTarget.parentElement.classList.add('hidden'); }} className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center">{icon}</button>))}
                  </div>
                </div>
                <input type="text" value={newFilterName} onChange={(e) => setNewFilterName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addClientFilter()} placeholder="New filter name..." className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" data-testid="new-client-filter-input" />
              </div>
              <button onClick={addClientFilter} disabled={!newFilterName.trim()} className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5" data-testid="add-client-filter-btn"><Plus size={14} />Add Filter</button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto space-y-2">
              {customClientFilters.map(cat => (
                <div key={cat.id} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group">
                  {editingFilter === cat.id ? (
                    <>
                      <span className="text-lg">{cat.icon}</span>
                      <input type="text" value={editFilterValue} onChange={(e) => setEditFilterValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') updateClientFilter(cat.id, editFilterValue); if (e.key === 'Escape') { setEditingFilter(null); setEditFilterValue(''); }}} className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm" autoFocus />
                      <button onClick={() => updateClientFilter(cat.id, editFilterValue)} className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"><Check size={14} /></button>
                      <button onClick={() => { setEditingFilter(null); setEditFilterValue(''); }} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <span className="w-8 h-8 rounded-lg bg-gray-700 flex items-center justify-center text-sm">{cat.icon}</span>
                      <span className="flex-1 text-white text-sm">{cat.name}{['A-Z', 'Budget'].includes(cat.id) && <span className="ml-2 text-xs text-gray-500">(default)</span>}</span>
                      <button onClick={() => { setEditingFilter(cat.id); setEditFilterValue(cat.name); }} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100"><Pencil size={14} /></button>
                      {!['A-Z', 'Budget'].includes(cat.id) && <button onClick={() => deleteClientFilter(cat.id)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-700">
              <button onClick={() => setShowClientFilterModal(false)} className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium" data-testid="done-client-filter-btn">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Filter Categories Modal */}
      {showCustomerFilterModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowCustomerFilterModal(false)}>
          <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Edit Customer Filters</h3>
              <button onClick={() => setShowCustomerFilterModal(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="p-4 border-b border-gray-700">
              <div className="flex gap-2 mb-3">
                <div className="relative">
                  <button className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center" onClick={(e) => e.currentTarget.nextElementSibling.classList.toggle('hidden')}>{newFilterIcon}</button>
                  <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-5 gap-1 w-40">
                    {filterIcons.map(icon => (<button key={icon} onClick={(e) => { setNewFilterIcon(icon); e.currentTarget.parentElement.classList.add('hidden'); }} className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center">{icon}</button>))}
                  </div>
                </div>
                <input type="text" value={newFilterName} onChange={(e) => setNewFilterName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addCustomerFilter()} placeholder="New filter name..." className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" data-testid="new-customer-filter-input" />
              </div>
              <button onClick={addCustomerFilter} disabled={!newFilterName.trim()} className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5" data-testid="add-customer-filter-btn"><Plus size={14} />Add Filter</button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto space-y-2">
              {customCustomerFilters.map(cat => (
                <div key={cat.id} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group">
                  {editingFilter === cat.id ? (
                    <>
                      <span className="text-lg">{cat.icon}</span>
                      <input type="text" value={editFilterValue} onChange={(e) => setEditFilterValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') updateCustomerFilter(cat.id, editFilterValue); if (e.key === 'Escape') { setEditingFilter(null); setEditFilterValue(''); }}} className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm" autoFocus />
                      <button onClick={() => updateCustomerFilter(cat.id, editFilterValue)} className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"><Check size={14} /></button>
                      <button onClick={() => { setEditingFilter(null); setEditFilterValue(''); }} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <span className="w-8 h-8 rounded-lg bg-gray-700 flex items-center justify-center text-sm">{cat.icon}</span>
                      <span className="flex-1 text-white text-sm">{cat.name}{['All', 'Active'].includes(cat.id) && <span className="ml-2 text-xs text-gray-500">(default)</span>}</span>
                      <button onClick={() => { setEditingFilter(cat.id); setEditFilterValue(cat.name); }} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100"><Pencil size={14} /></button>
                      {!['All', 'Active'].includes(cat.id) && <button onClick={() => deleteCustomerFilter(cat.id)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-700">
              <button onClick={() => setShowCustomerFilterModal(false)} className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium" data-testid="done-customer-filter-btn">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RealEstateClientsCustomers;
